import { AnimatePresence } from 'framer-motion';
import {
  AdminLogo,
  Arrow,
  VDriveLogo,
  XModalIcon,
  successPNG,
} from '../../assets';
import {
  ModalHeader,
  ModalOverlay,
  ModalStyled,
  SuccessImage,
  ModalScrollableStyled,
  ModalFooter,
  Image,
  ModalOverflowStyled,
  ModalGlowBorder,
  ModalShimmer,
  ModalGlowWrapper,
} from './modal.styled';
import { Div, H3, Icon, lightTheme, modalVariants, P } from '../../styles';
// import { useDisableBodyScroll } from '../../helpers';
import ReactDOM from 'react-dom';
import { PropsModal } from './modal'; // Import the type from modal.tsx
import React, { useEffect, useRef, useId } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

type ModalPortalProps = PropsModal & {
  children: React.ReactNode;
  isOpen: boolean;
  maxHeight?: string;
  minHeight?: string;
  maxHeightSM?: string;
  minHeightSM?: string;
  paddingSM?: string;
  onBack?: () => void;
  /** AI "working" treatment: animated gradient border + halo + shimmer sweep. */
  glow?: boolean;
};

export const ModalPortal: React.FC<ModalPortalProps> = ({
  children,
  isOpen,
  closeModal,
  logo = false,
  vDriveLogo = false,
  exitButton = true,
  successImage,
  header,
  width,
  footer,
  zIndex = 130,
  textAlign,
  overflow,
  modalOverflow,
  image,
  $numOfFloatingButtons,
  subtitle,
  overlayBorderRadius = 0,
  backgroundColor,
  padding,
  maxHeight,
  minHeight,
  maxHeightSM,
  minHeightSM,
  paddingSM,
  titleWithExit,
  onBack,
  titleWithTextComponent,
  glow,
}) => {
  // useDisableBodyScroll(isOpen);
  const modalRoot = document.getElementById('modal-root');

  // a11y: contain Tab focus inside the modal, ESC closes, focus
  // returns to the trigger element when the modal unmounts. The same ref
  // scopes the keyboard-reveal effect below to this modal's box.
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(dialogRef, { active: isOpen, onEscape: closeModal });

  /*
   * Keep the field being typed into visible while the phone keyboard is up.
   *
   * The box itself already re-centres against the visible viewport (the
   * --app-vv-center variable in ModalStyled), but that only moves the BOX:
   * the browser reveals a focused field once, at focus time, BEFORE the
   * keyboard has resized anything, so after the modal shrinks the field can
   * sit below the fold of the modal's own scroll area. Every visual-viewport
   * resize therefore redoes the reveal by hand. Inert wherever the keyboard
   * is not involved: on a desktop the visual viewport only resizes with the
   * window, and re-centring the focused field then is harmless.
   */
  useEffect(() => {
    if (!isOpen) return undefined;
    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    /* Synchronous on purpose, not deferred to a frame: the hook publishing
       the --app-vv-* variables subscribed to this event first, so by the time
       this listener runs they are set, and scrollIntoView's own forced layout
       resolves them. A requestAnimationFrame here would also simply never run
       on a hidden page. */
    const reveal = () => {
      const box = dialogRef.current;
      const active = document.activeElement;
      if (!box || !active || !box.contains(active)) return;
      const isField =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (!isField) return;
      active.scrollIntoView({ block: 'center', inline: 'nearest' });
    };

    viewport.addEventListener('resize', reveal);
    return () => {
      viewport.removeEventListener('resize', reveal);
    };
  }, [isOpen]);

  if (!isOpen || !modalRoot) return null;

  // Pick the most descriptive label available for screen readers.
  const ariaLabel =
    typeof header === 'string'
      ? undefined
      : typeof titleWithExit === 'string'
      ? undefined
      : 'Dialog';
  const ariaLabelledBy = header || titleWithExit ? titleId : undefined;

  // Opting into `glow` at all (even `glow={false}`) mounts the wrapper for good:
  // ModalGlowWrapper then owns the fixed centering + the open animation and the
  // modal box drops those. Toggling the wrapper instead would remount the modal
  // and replay that animation every time generating flips — a visible blink.
  const glowable = glow !== undefined;

  const motionProps = {
    variants: modalVariants,
    initial: 'initial',
    animate: 'animate',
    exit: 'exit',
  } as const;

  const box = (
    <ModalStyled
      key="modal"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      tabIndex={-1}
      {...(glowable ? {} : motionProps)}
      $inWrapper={glowable}
      $width={width}
      $zIndex={zIndex}
      $textAlign={'left'}
      $modalOverflow={modalOverflow}
      $backgroundColor={backgroundColor}
    >
      {header && (
        <ModalHeader>
          <Div $flex $alignCenter $gap="16px">
            {onBack && (
              <Icon
                $rotate="180deg"
                $pointer
                onClick={onBack}
                role="button"
                tabIndex={0}
                aria-label="Back"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onBack();
                  }
                }}
              >
                <Arrow />
              </Icon>
            )}
            <div>
              <H3 id={titleId} $m="0" $color={lightTheme.white}>
                {header}
              </H3>
              {subtitle && (
                <P $m="0" $color={lightTheme.white}>
                  {subtitle}
                </P>
              )}
            </div>
          </Div>
        </ModalHeader>
      )}

      {footer && <ModalFooter>{footer}</ModalFooter>}

      <ModalScrollableStyled
        $overflow={overflow}
        $maxHeight={maxHeight}
        $minHeight={minHeight}
        $maxHeightSM={maxHeightSM}
        $minHeightSM={minHeightSM}
      >
        <ModalOverflowStyled
          $logo={logo}
          $vDriveLogo={vDriveLogo}
          $successImage={successImage || !!image}
          $footer={!!footer}
          $numOfFloatingButtons={$numOfFloatingButtons}
          $overflow={overflow}
          $padding={padding}
          $paddingSM={paddingSM}
        >
          {titleWithExit && (
            // With a subtitle under it the title block is two lines tall, and
            // centring the X against it left the X floating mid-height; it
            // belongs in the corner. A one-line title stays centred.
            <Div
              $flex
              $between
              $alignCenter={!subtitle}
              $alignStart={!!subtitle}
              $mb="24"
            >
              {/* `subtitle` renders here as well as under `header`, so the
                  white-header and title-with-exit variants read the same way.
                  Sits inside the title block rather than above `children` so
                  it stays tied to the heading instead of drifting down with
                  the modal's own content padding. `aria-labelledby` still
                  points at the H3 alone, so the subtitle does not end up in
                  the dialog's accessible name. */}
              <div>
                <H3 id={titleId} $m="0" $left>
                  {titleWithExit}
                </H3>
                {subtitle && (
                  <P $m="0" $left $color={lightTheme.lightGray}>
                    {subtitle}
                  </P>
                )}
              </div>
              <Div $flex $alignCenter $gap="16px">
                {titleWithTextComponent && titleWithTextComponent}
                <button
                  type="button"
                  aria-label="Close dialog"
                  onClick={() => closeModal?.()}
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon $pointer>
                    <svg
                      width="18"
                      height="19"
                      viewBox="0 0 18 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="redSvgHover"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M17.4716 3.6638C18.1747 2.94469 18.1747 1.77687 17.4716 1.05776C16.7684 0.338656 15.6266 0.338656 14.9234 1.05776L9.00031 7.12126L3.07156 1.06351C2.36844 0.344409 1.22656 0.344409 0.523437 1.06351C-0.179687 1.78262 -0.179687 2.95045 0.523437 3.66955L6.45219 9.72729L0.529062 15.7908C-0.174063 16.5099 -0.174063 17.6777 0.529062 18.3968C1.23219 19.1159 2.37406 19.1159 3.07719 18.3968L9.00031 12.3333L14.9291 18.3911C15.6322 19.1102 16.7741 19.1102 17.4772 18.3911C18.1803 17.672 18.1803 16.5041 17.4772 15.785L11.5484 9.72729L17.4716 3.6638Z"
                        fill={header ? lightTheme.white : lightTheme.lightGray}
                      />
                    </svg>
                  </Icon>
                </button>
              </Div>
            </Div>
          )}
          {children}
        </ModalOverflowStyled>
      </ModalScrollableStyled>

      {logo && <AdminLogo />}
      {vDriveLogo && <VDriveLogo width="160" height="160" />}
      {successImage && <SuccessImage src={successPNG} alt="success" />}
      {image && <Image src={image} alt="image" />}

      {!titleWithExit && exitButton && (
        <button
          type="button"
          aria-label="Close dialog"
          onClick={() => closeModal?.()}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            cursor: 'pointer',
            background: 'transparent',
            border: 0,
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <XModalIcon
            color={header ? lightTheme.white : lightTheme.lightGray}
          />
        </button>
      )}

      {glowable && (
        <>
          <ModalShimmer $active={glow} />
          <ModalGlowBorder $active={glow} />
        </>
      )}
    </ModalStyled>
  );

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      <>
        {glowable ? (
          <ModalGlowWrapper
            key="modal"
            {...motionProps}
            $zIndex={zIndex}
            $width={width}
            $active={glow}
          >
            {box}
          </ModalGlowWrapper>
        ) : (
          box
        )}

        <ModalOverlay
          key="modalOverlay"
          aria-hidden="true"
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => closeModal?.()}
          $zIndex={zIndex}
          style={{ borderRadius: overlayBorderRadius }}
        />
      </>
    </AnimatePresence>,
    modalRoot
  );
};
